import { StorageData, storageSchema } from "@shared/schema";

const STORAGE_KEY = "cm:v1";

export class LocalStorageManager {
  private static instance: LocalStorageManager;
  
  private constructor() {}
  
  static getInstance(): LocalStorageManager {
    if (!LocalStorageManager.instance) {
      LocalStorageManager.instance = new LocalStorageManager();
    }
    return LocalStorageManager.instance;
  }
  
  getData(): StorageData {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return this.getDefaultData();
      }
      
      const parsed = JSON.parse(stored);
      return storageSchema.parse(parsed);
    } catch (error) {
      console.error("Error loading data from localStorage:", error);
      return this.getDefaultData();
    }
  }
  
  setData(data: StorageData): void {
    try {
      const validated = storageSchema.parse(data);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(validated));
    } catch (error) {
      console.error("Error saving data to localStorage:", error);
      throw error;
    }
  }
  
  private getDefaultData(): StorageData {
    return {
      types: [],
      codes: [],
      typeEdges: [],
      audit: [],
      settings: {
        canonicalizeCode: true,
        enforceUniqueNames: true,
        nlpProvider: "LocalRules",
        autoSave: true,
        theme: "light"
      },
      ai: {
        queries: [],
        savedQueries: []
      }
    };
  }
  
  reset(): void {
    window.localStorage.removeItem(STORAGE_KEY);
  }
  
  seedData(): void {
    const seedData: StorageData = {
      types: [
        {
          key: "CUSTOMER",
          name: "Customer Types",
          description: "Customer classification types",
          displayName: "Customers",
          parentType: "TYPES",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          key: "PRODUCT",
          name: "Product Types",
          description: "Product classification system",
          displayName: "Products",
          parentType: "TYPES",
          seq: 20,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          key: "SUBSCRIPTION",
          name: "Subscription Types",
          description: "Subscription plan categories",
          displayName: "Subscriptions",
          parentType: "TYPES",
          seq: 30,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          key: "STATUS",
          name: "Status Types",
          description: "General status classifications",
          displayName: "Status",
          parentType: "TYPES",
          seq: 40,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          key: "COUNTRY",
          name: "Country Types",
          description: "Country and nation classifications",
          displayName: "Countries",
          parentType: "TYPES",
          seq: 50,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          key: "STATE",
          name: "State Types",
          description: "State and province classifications",
          displayName: "States",
          parentType: "COUNTRY",
          seq: 60,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          key: "CITY",
          name: "City Types",
          description: "City and municipality classifications",
          displayName: "Cities",
          parentType: "STATE",
          seq: 70,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          key: "CATEGORY",
          name: "Category Types",
          description: "Main category classifications",
          displayName: "Categories",
          parentType: "TYPES",
          seq: 80,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          key: "SUBCATEGORY",
          name: "Subcategory Types",
          description: "Subcategory classifications under categories",
          displayName: "Subcategories",
          parentType: "CATEGORY",
          seq: 90,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          key: "LT",
          name: "LT Types",
          description: "LT level classifications under subcategories",
          displayName: "LT Items",
          parentType: "SUBCATEGORY",
          seq: 100,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        }
      ],
      codes: [
        // Customer codes
        {
          type: "CUSTOMER",
          code: "INDIVIDUAL",
          name: "Individual Customer",
          description: "Single person customer account",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "CUSTOMER",
          code: "BUSINESS",
          name: "Business Customer",
          description: "Corporate or business customer",
          seq: 20,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "CUSTOMER",
          code: "ENTERPRISE",
          name: "Enterprise Customer",
          description: "Large enterprise customer with special terms",
          seq: 30,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        // Product codes
        {
          type: "PRODUCT",
          code: "SOFTWARE",
          name: "Software Products",
          description: "Digital software solutions",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "PRODUCT",
          code: "HARDWARE",
          name: "Hardware Products",
          description: "Physical hardware devices",
          seq: 20,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "PRODUCT",
          code: "SERVICE",
          name: "Service Products",
          description: "Professional services and consulting",
          seq: 30,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        // Subscription codes
        {
          type: "SUBSCRIPTION",
          code: "BASIC",
          name: "Basic Plan",
          description: "Entry-level subscription plan",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "SUBSCRIPTION",
          code: "PREMIUM",
          name: "Premium Plan",
          description: "Enhanced features subscription plan",
          seq: 20,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "SUBSCRIPTION",
          code: "ENTERPRISE",
          name: "Enterprise Plan",
          description: "Full-featured enterprise subscription",
          seq: 30,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        // Status codes
        {
          type: "STATUS",
          code: "ACTIVE",
          name: "Active",
          description: "Currently active and operational",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "STATUS",
          code: "INACTIVE",
          name: "Inactive",
          description: "Temporarily disabled or inactive",
          seq: 20,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "STATUS",
          code: "PENDING",
          name: "Pending",
          description: "Awaiting approval or processing",
          seq: 30,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        // Country codes
        {
          type: "COUNTRY",
          code: "US",
          name: "United States",
          description: "United States of America",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "COUNTRY",
          code: "CA",
          name: "Canada",
          description: "Canada",
          seq: 20,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "COUNTRY",
          code: "MX",
          name: "Mexico",
          description: "United Mexican States",
          seq: 30,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "COUNTRY",
          code: "IN",
          name: "India",
          description: "Republic of India",
          seq: 40,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        // State codes
        {
          type: "STATE",
          code: "CA",
          name: "California",
          description: "State of California",
          parentType: "COUNTRY",
          parentCode: "US",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "STATE",
          code: "NY",
          name: "New York",
          description: "State of New York",
          parentType: "COUNTRY",
          parentCode: "US",
          seq: 20,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "STATE",
          code: "TX",
          name: "Texas",
          description: "State of Texas",
          parentType: "COUNTRY",
          parentCode: "US",
          seq: 30,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "STATE",
          code: "ON",
          name: "Ontario",
          description: "Province of Ontario",
          parentType: "COUNTRY",
          parentCode: "CA",
          seq: 40,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "STATE",
          code: "DL",
          name: "Delhi",
          description: "National Capital Territory of Delhi",
          parentType: "COUNTRY",
          parentCode: "IN",
          seq: 50,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        // City codes
        {
          type: "CITY",
          code: "SF",
          name: "San Francisco",
          description: "City of San Francisco",
          parentType: "STATE",
          parentCode: "CA",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "CITY",
          code: "LA",
          name: "Los Angeles",
          description: "City of Los Angeles",
          parentType: "STATE",
          parentCode: "CA",
          seq: 20,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "CITY",
          code: "NYC",
          name: "New York City",
          description: "City of New York",
          parentType: "STATE",
          parentCode: "NY",
          seq: 30,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "CITY",
          code: "TOR",
          name: "Toronto",
          description: "City of Toronto",
          parentType: "STATE",
          parentCode: "ON",
          seq: 40,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        // Category codes
        {
          type: "CATEGORY",
          code: "TECH",
          name: "Technology",
          description: "Technology and IT related categories",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "CATEGORY",
          code: "FINANCE",
          name: "Finance",
          description: "Financial services and products",
          seq: 20,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "CATEGORY",
          code: "RETAIL",
          name: "Retail",
          description: "Retail and e-commerce categories",
          seq: 30,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        // Subcategory codes
        {
          type: "SUBCATEGORY",
          code: "CLOUD",
          name: "Cloud Computing",
          description: "Cloud infrastructure and services",
          parentType: "CATEGORY",
          parentCode: "TECH",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "SUBCATEGORY",
          code: "AI",
          name: "Artificial Intelligence",
          description: "AI and machine learning solutions",
          parentType: "CATEGORY",
          parentCode: "TECH",
          seq: 20,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "SUBCATEGORY",
          code: "BANKING",
          name: "Banking Services",
          description: "Traditional and digital banking",
          parentType: "CATEGORY",
          parentCode: "FINANCE",
          seq: 30,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "SUBCATEGORY",
          code: "ECOMMERCE",
          name: "E-commerce",
          description: "Online retail platforms",
          parentType: "CATEGORY",
          parentCode: "RETAIL",
          seq: 40,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        // LT codes
        {
          type: "LT",
          code: "AWS",
          name: "Amazon Web Services",
          description: "AWS cloud platform services",
          parentType: "SUBCATEGORY",
          parentCode: "CLOUD",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "LT",
          code: "AZURE",
          name: "Microsoft Azure",
          description: "Microsoft cloud platform",
          parentType: "SUBCATEGORY",
          parentCode: "CLOUD",
          seq: 20,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "LT",
          code: "OPENAI",
          name: "OpenAI Services",
          description: "OpenAI API and models",
          parentType: "SUBCATEGORY",
          parentCode: "AI",
          seq: 30,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        {
          type: "LT",
          code: "CHATGPT",
          name: "ChatGPT",
          description: "ChatGPT conversational AI",
          parentType: "SUBCATEGORY",
          parentCode: "AI",
          seq: 40,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        },
        
        // === PROBLEMATIC DATA FOR DQ TESTING ===
        // Duplicate name within same type (CUSTOMER)
        {
          type: "CUSTOMER",
          code: "DUPLICATE_TEST",
          name: "Individual Customer", // Same name as existing CUSTOMER code
          description: "This creates a duplicate name issue for DQ testing",
          seq: 99,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "test-user",
          isDeleted: false
        },
        // Another duplicate name in PRODUCT type
        {
          type: "PRODUCT",
          code: "DUPLICATE_SOFT",
          name: "Software Products", // Same name as existing PRODUCT code
          description: "This creates another duplicate name issue",
          seq: 99,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "test-user",
          isDeleted: false
        },
        // Orphaned code - references non-existent type
        {
          type: "NONEXISTENT_TYPE",
          code: "ORPHAN1",
          name: "Orphaned Code 1",
          description: "This code references a type that doesn't exist",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "test-user",
          isDeleted: false
        },
        // Another orphaned code
        {
          type: "MISSING_TYPE",
          code: "ORPHAN2",
          name: "Orphaned Code 2",
          description: "Another orphaned code for testing DQ scan",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "test-user",
          isDeleted: false
        },
        // Third duplicate in STATUS type
        {
          type: "STATUS",
          code: "DUPLICATE_ACTIVE",
          name: "Active", // Same name as existing STATUS code
          description: "Creates duplicate in STATUS type for testing",
          seq: 99,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "test-user",
          isDeleted: false
        },
        
        // === MORE TEST DATA FOR MANUAL DQ TESTING ===
        // Additional duplicate in SUBSCRIPTION type
        {
          type: "SUBSCRIPTION",
          code: "DUPLICATE_BASIC",
          name: "Basic Plan", // Same name as existing SUBSCRIPTION code
          description: "Another duplicate for testing DQ scan",
          seq: 99,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "qa-tester",
          isDeleted: false
        },
        // More orphaned codes with different types
        {
          type: "INVALID_TYPE_X",
          code: "ORPHAN_X1",
          name: "Orphaned Item X1",
          description: "References completely invalid type X",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "qa-tester",
          isDeleted: false
        },
        {
          type: "BOGUS_TYPE",
          code: "ORPHAN_Y2", 
          name: "Orphaned Item Y2",
          description: "References bogus type that doesn't exist",
          seq: 10,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "qa-tester",
          isDeleted: false
        },
        // Duplicate in LT type
        {
          type: "LT",
          code: "DUPLICATE_AWS",
          name: "Amazon Web Services", // Same name as existing LT code
          description: "Duplicate AWS entry for testing",
          seq: 99,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "qa-tester",
          isDeleted: false
        },
        // Another duplicate in COUNTRY type
        {
          type: "COUNTRY",
          code: "DUPLICATE_US",
          name: "United States", // Same name as existing COUNTRY code
          description: "Duplicate country entry",
          seq: 99,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "qa-tester",
          isDeleted: false
        },
        // Overlap example: same name across different types (PRODUCT vs SUBCATEGORY)
        {
          type: "PRODUCT",
          code: "CLOUD",
          name: "Cloud Computing",
          description: "Overlap test: same display name exists in SUBCATEGORY",
          seq: 98,
          useFlag: true,
          createdAt: new Date().toISOString(),
          createdBy: "system",
          isDeleted: false
        }
      ],
      typeEdges: [
        {
          fromType: "CUSTOMER",
          toType: "SUBSCRIPTION",
          seq: 10,
          isRequired: false,
          createdAt: new Date().toISOString()
        },
        {
          fromType: "SUBSCRIPTION",
          toType: "PRODUCT",
          seq: 20,
          isRequired: false,
          createdAt: new Date().toISOString()
        },
        {
          fromType: "COUNTRY",
          toType: "STATE",
          seq: 30,
          isRequired: false,
          createdAt: new Date().toISOString()
        },
        {
          fromType: "STATE",
          toType: "CITY",
          seq: 40,
          isRequired: false,
          createdAt: new Date().toISOString()
        },
        {
          fromType: "CATEGORY",
          toType: "SUBCATEGORY",
          seq: 50,
          isRequired: false,
          createdAt: new Date().toISOString()
        },
        {
          fromType: "SUBCATEGORY",
          toType: "LT",
          seq: 60,
          isRequired: false,
          createdAt: new Date().toISOString()
        },
        // Cycle example: LT -> CATEGORY closes a loop CATEGORY -> SUBCATEGORY -> LT -> CATEGORY
        {
          fromType: "LT",
          toType: "CATEGORY",
          seq: 99,
          isRequired: false,
          createdAt: new Date().toISOString()
        }
      ],
      audit: [
        // Type creation audit entries
        {
          id: "audit-type-1",
          action: "CREATE",
          entityType: "TYPE",
          entityKey: "CUSTOMER",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-type-2",
          action: "CREATE",
          entityType: "TYPE",
          entityKey: "PRODUCT",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-type-3",
          action: "CREATE",
          entityType: "TYPE",
          entityKey: "SUBSCRIPTION",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-type-4",
          action: "CREATE",
          entityType: "TYPE",
          entityKey: "STATUS",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-type-5",
          action: "CREATE",
          entityType: "TYPE",
          entityKey: "COUNTRY",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-type-6",
          action: "CREATE",
          entityType: "TYPE",
          entityKey: "STATE",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-type-7",
          action: "CREATE",
          entityType: "TYPE",
          entityKey: "CITY",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-type-8",
          action: "CREATE",
          entityType: "TYPE",
          entityKey: "CATEGORY",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-type-9",
          action: "CREATE",
          entityType: "TYPE",
          entityKey: "SUBCATEGORY",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-type-10",
          action: "CREATE",
          entityType: "TYPE",
          entityKey: "LT",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        // Code creation audit entries
        {
          id: "audit-code-1",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "CUSTOMER:INDIVIDUAL",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-2",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "CUSTOMER:BUSINESS",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-3",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "CUSTOMER:ENTERPRISE",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-4",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "PRODUCT:SOFTWARE",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-5",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "PRODUCT:HARDWARE",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-6",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "PRODUCT:SERVICE",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-7",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "SUBSCRIPTION:BASIC",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-8",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "SUBSCRIPTION:PREMIUM",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-9",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "SUBSCRIPTION:ENTERPRISE",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-10",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "STATUS:ACTIVE",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-11",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "STATUS:INACTIVE",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-12",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "STATUS:PENDING",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-13",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "COUNTRY:US",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-14",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "COUNTRY:CA",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-15",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "COUNTRY:MX",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-16",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "COUNTRY:IN",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-17",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "STATE:CA",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-18",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "STATE:NY",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-19",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "STATE:TX",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-20",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "STATE:ON",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-21",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "STATE:DL",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-22",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "CITY:SF",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-23",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "CITY:LA",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-24",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "CITY:NYC",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-25",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "CITY:TOR",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-26",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "CATEGORY:TECH",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-27",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "CATEGORY:FINANCE",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-28",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "CATEGORY:RETAIL",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-29",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "SUBCATEGORY:CLOUD",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-30",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "SUBCATEGORY:AI",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-31",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "SUBCATEGORY:BANKING",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-32",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "SUBCATEGORY:ECOMMERCE",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-33",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "LT:AWS",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-34",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "LT:AZURE",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-35",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "LT:OPENAI",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        {
          id: "audit-code-36",
          action: "CREATE",
          entityType: "CODE",
          entityKey: "LT:CHATGPT",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed"
        },
        // Type edge creation audit entries
        {
          id: "audit-edge-1",
          action: "CREATE",
          entityType: "TYPE_EDGE",
          entityKey: "TYPES->CUSTOMER",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed - type relationship"
        },
        {
          id: "audit-edge-2",
          action: "CREATE",
          entityType: "TYPE_EDGE",
          entityKey: "TYPES->PRODUCT",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed - type relationship"
        },
        {
          id: "audit-edge-3",
          action: "CREATE",
          entityType: "TYPE_EDGE",
          entityKey: "COUNTRY->STATE",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed - type relationship"
        },
        {
          id: "audit-edge-4",
          action: "CREATE",
          entityType: "TYPE_EDGE",
          entityKey: "STATE->CITY",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed - type relationship"
        },
        {
          id: "audit-edge-5",
          action: "CREATE",
          entityType: "TYPE_EDGE",
          entityKey: "CATEGORY->SUBCATEGORY",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed - type relationship"
        },
        {
          id: "audit-edge-6",
          action: "CREATE",
          entityType: "TYPE_EDGE",
          entityKey: "SUBCATEGORY->LT",
          timestamp: new Date().toISOString(),
          user: "system",
          note: "Initial system data seed - type relationship"
        }
      ],
      settings: {
        canonicalizeCode: true,
        enforceUniqueNames: true,
        nlpProvider: "LocalRules",
        autoSave: true,
        theme: "light"
      },
      ai: {
        queries: [],
        savedQueries: []
      }
    };
    
    this.setData(seedData);
  }
}

export const localStorage = LocalStorageManager.getInstance();
